import * as React from 'react';
import { useMemo } from 'react';
import { createTheme, adaptV4Theme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, Theme, StyledEngineProvider } from '@mui/material/';


declare module '@mui/styles/defaultTheme' {
  interface DefaultTheme extends Theme {}
}
declare module '@mui/material/styles' {
  interface Theme {
    languageDrawer: {
      width: React.CSSProperties['width'];
    };
  }
  interface Palette {
    titleColor: string;
    headerBackground: string;
    selectedBackground: string;
    border: string;
  }
  interface PaletteOptions {
    titleColor?: string;
    headerBackground?: string;
    selectedBackground?: string;
    border?: string;
  }
  interface Typography {
    listTitle: React.CSSProperties;
    usersFontTitle: React.CSSProperties;
    columnHeader: React.CSSProperties;
    usersFontRegular: React.CSSProperties;
    smallTitle: React.CSSProperties;
    placeHolderFont: React.CSSProperties;
    buttons: React.CSSProperties;
  }
  interface TypographyOptions {
    listTitle?: React.CSSProperties;
    usersFontTitle?: React.CSSProperties;
    columnHeader?: React.CSSProperties;
    usersFontTitleRegular?: React.CSSProperties;
    smallTitle?: React.CSSProperties;
    placeHolderFont?: React.CSSProperties;
    buttons?: React.CSSProperties;
  }
  interface TypographyVariants {
    listTitle: React.CSSProperties;
    usersFontTitle: React.CSSProperties;
    columnHeader: React.CSSProperties;
    usersFontRegular: React.CSSProperties;
    smallTitle: React.CSSProperties;
    placeHolderFont: React.CSSProperties;
    buttons: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    listTitle?: React.CSSProperties;
    usersFontTitle?: React.CSSProperties;
    columnHeader?: React.CSSProperties;
    usersFontRegular?: React.CSSProperties;
    smallTitle?: React.CSSProperties;
    placeHolderFont?: React.CSSProperties;
    buttons?: React.CSSProperties;
  }
  // allow configuration using `createMuiTheme`
  interface DeprecatedThemeOptions {
    languageDrawer?: {
      width?: React.CSSProperties['width'];
    };
  }
}

interface Props {
  children?: React.ReactNode;
}

const UITheme: React.FC<Props> = ({ children }) => {
  const theme = useMemo(() => {
    return createTheme(adaptV4Theme({
      palette: {
        primary: {
          main: '#1F9122',
          light: '#50B948',
        },
        secondary: {
          main: '#4C4D4F',
        },
        common: {
          black: '#000000',
          white: '#FFFFFF',
        },
        titleColor: 'var(--V_darkgray_6D6D6D, #6D6D6D)',
        headerBackground: 'var(--V_gray_F5F5F5, #F5F5F5)',
        selectedBackground: '#F5F5F5',
        border: '1px solid var(--V_midgray_DDDDDD, #DDD)'
      },
      typography: {
        fontFamily: 'Maven Pro, sans-serif',
        listTitle: {
          fontFamily: 'Maven Pro, sans-serif',
          fontSize: 24,
          fontWeight: 600,
          fontStyle: 'normal',
          lineHeight: 'normal',
          textTransform: 'uppercase',
          marginTop: '24px',
          marginBottom: '24px',
        },
        usersFontTitle:{
          color: 'var(--V_black_262626, #262626)',
          fontFamily: 'Maven Pro',
          fontSize: 16,
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: 'normal'
        },
        columnHeader: {
          color: 'var(--V_black_262626, #262626)',
          fontFamily: 'Maven Pro',
          fontSize: 14,
          fontStyle: 'normal',
          fontWeight: 600,
          lineHeight: 'normal',
        },
        usersFontRegular:{
          color: '#000',
          fontFamily: "Maven Pro",
          fontSize: 14,
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: 'normal'
        },
        smallTitle: {
          color: '#262626',
          fontFamily: 'Maven Pro',
          fontSize: 12,
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: 'normal',
        },
        placeHolderFont: {
          color: '#6D6D6D',
          textAlign: 'center',
          fontFamily: '"Maven Pro"',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: 'normal',
        },
        buttons: {
          display: 'flex',
          height: '24px',
          padding: '4px 8px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          flexShrink: 0,
          textTransform: 'none',
          borderRadius: 12,
        }
      },
    }));
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      (<ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>)
    </StyledEngineProvider>
  );
};

export default UITheme;
